import { Share } from "@/assets/icons";
import {
  AreasDetailsSummary,
  Button,
  ButtonSize,
  ButtonVariant,
  ContractorDetailsSummary,
  EmptyDataScreen,
  ErrorScreen,
  History,
  Loader,
  Panel,
  Row,
  RowSize,
  VisitDetailsSummary,
  toast,
} from "@/components";
import clsx from "clsx";
import { FC, useContext, useEffect, useState } from "react";
import styles from "./styles.scss";
import { RequestDetailsProps, errorMessage } from "./types";

import { AttachmentsSummary } from "@/components/attachments-summary/attachments-summary";
import { BasicRoutePath } from "@/constants";
import { AuthContext } from "@/guards/auth/auth-context";
import { useRequestState } from "@/hooks";
import {
  requestActionsMap,
  requestActionsMapProps,
} from "@/pages/inbox/components/inbox-details/request-action-map";
import { InboxKeyParam } from "@/pages/inbox/types";
import { useBoundStore } from "@/services";
import {
  cancelInboxRequest,
  requestInboxDetails,
  submitInboxRequest,
} from "@/services/api/inbox/requests";
import { RequestResponse } from "@/services/api/request";
import { selectInboxStore } from "@/services/stores/selectors/inbox";
import { StatusCodes, getReasonPhrase } from "http-status-codes";
import { get, map, size, toLower, isString } from "lodash";
import { useNavigate, useSearchParams } from "react-router-dom";

export const InboxDetails: FC<RequestDetailsProps> = ({
  selectedRequests: data,
  onGetSelectedRequestData,
  onResetSelectedRequest,
  onRemoveRequestFromList,
}) => {
  const { activeInboxItem } = useBoundStore(selectInboxStore);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { account } = useContext(AuthContext);
  const [isLocked, setIsLocked] = useState(false);

  const referenceNumber = searchParams.get(InboxKeyParam.ReferenceNumber);
  const domainId = searchParams.get(InboxKeyParam.DomainId);
  const requestId = activeInboxItem?.RequestId;

  const {
    isLoading,
    data: details,
    error,
    setInProgress,
    setSuccess,
    setError,
  } = useRequestState<RequestResponse>({ isLoading: true });

  useEffect(() => {
    const controller = new AbortController();

    if (referenceNumber && domainId) {
      setInProgress();
      requestInboxDetails(
        { email: account?.username ?? "", referenceNumber, domainId },
        { signal: controller.signal },
      )
        .then(response => {
          setSuccess(response?.Data);
        })
        .catch(_error => {
          setError(_error);
        });
    }

    return () => {
      controller.abort();
    };
  }, [account, referenceNumber, setError, setSuccess, setInProgress, domainId]);

  const handleGoBack = () => onResetSelectedRequest();

  const handleEdit = () => {
    if (referenceNumber) {
      navigate(
        `${BasicRoutePath.Requests}/edit/${encodeURIComponent(
          referenceNumber,
        )}/${domainId}`,
      );
    }
  };

  const handleCancelRequest = () => {
    if (referenceNumber) {
      setIsLocked(true);
      cancelInboxRequest({
        email: account?.username ?? "",
        requestId: referenceNumber,
      })
        .then(response => {
          const referenceNumber = response?.ReferenceNo;

          onRemoveRequestFromList(referenceNumber);

          onResetSelectedRequest();

          toast.success("Request has been cancelled successfully");
        })
        .catch(error => {
          toast.error(
            error.message || getReasonPhrase(StatusCodes.INTERNAL_SERVER_ERROR),
          );
        })
        .finally(() => {
          setIsLocked(false);
        });
    }
  };

  const handleSubmitRequest = () => {
    if (referenceNumber) {
      const requestDetailsData = onGetSelectedRequestData(referenceNumber);

      setIsLocked(true);
      submitInboxRequest({
        email: account?.username ?? "",
        loaRequestUpdateDto: {
          RequestId: requestDetailsData?.RequestId || 0,
          ActionId: 0,
          NextAgentId: 0,
          Remarks: requestDetailsData?.Remarks || "",
          Email: "",
        },
      })
        .catch(error => {
          toast.error(
            error.message || getReasonPhrase(StatusCodes.INTERNAL_SERVER_ERROR),
          );
        })
        .finally(() => {
          setIsLocked(false);
        });
    }
  };

  if (isLoading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (error) {
    const errorText = error as errorMessage;

    const errorMsg = isString(errorText.message)
      ? errorText.message
      : errorText.message[0] ||
        getReasonPhrase(StatusCodes.INTERNAL_SERVER_ERROR);

    return <ErrorScreen subtitle={errorMsg} error={error} />;
  }

  if (!size(details) && !error) {
    return <EmptyDataScreen />;
  }

  const areasData = size(details?.ApplicationArea)
    ? details?.ApplicationArea
    : details?.Lookups.AreaLookup?.Areas;

  const _requestId = requestId || details?.LOARequestBasicDetails?.Request_ID;

  const footerActions =
    requestActionsMap[
      toLower(activeInboxItem?.RequestStatus) as keyof typeof requestActionsMap
    ];

  return (
    <div className={clsx(styles.detailsWrapper, data && styles.selected)}>
      <div className={styles.details}>
        <Row size={RowSize.Large} className={styles.title}>
          Request {_requestId ? `ID: ${_requestId}` : ""}
        </Row>

        <Row size={RowSize.Large}>
          <ContractorDetailsSummary
            data={{
              CompanyName:
                details?.ImmigrationRequestBasicDetails?.CompanyName ||
                activeInboxItem?.CompanyName,
              ContractNo:
                details?.LOARequestBasicDetails?.Contract_Number ||
                activeInboxItem?.ContractNo,
              SubContractor: get(
                details,
                "Lookups.LoaSubContractorLookup.SubContractors[0].CompanyNameEn",
              ),
              ContractAdministrator: get(
                details,
                "Lookups.LoaContractsLookup.LoaContracts[0].Employee.NameEn",
              ),
            }}
          />
        </Row>

        <Row size={RowSize.Large}>
          <VisitDetailsSummary
            data={details?.LOARequestBasicDetails}
            inboxWorkersData={details?.LoaWorkers?.LoaWorkers}
          />
        </Row>

        <Row size={RowSize.Large}>
          <AreasDetailsSummary areas={areasData} />
        </Row>

        <Row size={RowSize.Large}>
          <AttachmentsSummary
            requestId={details?.LOARequestBasicDetails?.Request_ID}
            applications={details?.Lookups.ApplicationLookup?.Applications}
          />
        </Row>

        <Row size={RowSize.Large}>
          <History history={details?.ApplicationHistory} />
        </Row>
      </div>

      <div
        className={clsx(
          styles.detailsFooter,
          !size(footerActions) && styles.isHidden,
        )}
      >
        <Row size={RowSize.Large}>
          <Panel panelInnerClassName={styles.actionButtonsGroup}>
            <div>
              <Button
                disabled={isLocked}
                className={styles.goBackBtn}
                variant={ButtonVariant.Secondary}
                size={ButtonSize.Small}
                icon={
                  <Share
                    className={clsx(styles.actionButtonIcon, styles.reverse)}
                  />
                }
                onClick={handleGoBack}
              >
                Close
              </Button>
            </div>

            <Row className={styles.innerButtonsGroup}>
              {map(footerActions, (item, index) => {
                return (
                  <Button
                    key={index}
                    {...requestActionsMapProps[item]({
                      isLocked,
                      handleCancelRequest,
                      handleSubmitRequest,
                      handleEdit,
                    })}
                  >
                    {item}
                  </Button>
                );
              })}
            </Row>
          </Panel>
        </Row>
      </div>
    </div>
  );
};
